import { createTheme } from "@mui/material/styles";
import theme from "../theme";

const baseDarkTheme = createTheme({
  palette: {
    primary: {
      light: "#FE605D",
      main: "#FF310C",
      dark: "#FE0000",
      contrastText: "#FFF",
    },
    secondary: {
      light: "#393070",
      main: "#0279E1",
      dark: "#005BBB",
      contrastText: "#FFF",
    },
    success: {
      light: "#afb555",
      main: "##0b8967",
      dark: "#4CB200",
      contrastText: "#FFF",
    },
    info: {
      light: "#32BAFF",
      main: "#8B8B8B ",
      dark: "#8B8B8B",
      contrastText: "#FFF",
    },
    error: {
      light: "#FF6166",
      main: "#FF0000",
      dark: "#E04347",
      contrastText: "#FFF",
    },
    warning: {
      light: "#FFCA64",
      main: "#FFB400",
      dark: "#E09E00",
      contrastText: "#FFF",
    },
    grey: {
      50: "#FAFAFA",
      100: "#F5F5F5",
      200: "#EEEEEE",
      300: "#E0E0E0",
      400: "#BDBDBD",
      500: "#9E9E9E",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
      A100: "#D5D5D5",
      A200: "#AAAAAA",
      A400: "#616161",
      A700: "#303030",
    },
    text: {
      primary: "#000000",
      secondary: "#ffffff",
    },
    action: {
      disabledBackground: "rgba(73,82,88,0.12)",
      hoverOpacity: 0.02,
      hover: "#f6f9fc",
    },
    divider: "#e5eaef",
    background: {
      default: "#eef5f9",
      paper: "#F2F7FC",
    },
  },
  typography: {
    fontFamily: "Poppins, Helvetica, Arial, sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: "2.25rem",
      lineHeight: "2.75rem",
    },
    h2: {
      fontWeight: 700,
      fontSize: "1.875rem",
      lineHeight: "2.25rem",
    },
    h3: {
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: "1.75rem",
    },
    h4: {
      fontWeight: 700,
      fontSize: "1.3125rem",
      lineHeight: "1.6rem",
    },
    h5: {
      fontWeight: 700,
      fontSize: "1.125rem",
      lineHeight: "1.6rem",
    },
    h6: {
      fontWeight: 700,
      fontSize: "1rem",
      lineHeight: "1.2rem",
    },
    button: {
      textTransform: "capitalize",
      fontWeight: 400,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.334rem",
    },
    body2: {
      fontSize: "1rem",
      letterSpacing: "0rem",
      fontWeight: 400,
      lineHeight: "1rem",
    },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "1.6rem",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 400,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ".MuiPaper-elevation9, .MuiPopover-root .MuiPaper-elevation": {
          boxShadow:
            "rgba(145 158 171 / 30%) 0px 0px 2px 0px, rgba(145 158 171 / 12%) 0px 12px 24px -4px !important",
        },
        a: {
          textDecoration: "none",
        },
        ".MuiTimelineConnector-root": {
          width: "1px !important",
          backgroundColor: "rgba(0, 0, 0, 0.12) !important",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `1px solid ${theme.palette.grey[300]}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          padding: "4px 12px",
          fontSize: "1.25rem",
          fontWeight: 700,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 700,
          borderRadius: "12px",
          padding: "0px",
        },
        expandIcon: {
          color: theme.palette.primary.contrastText,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0px 12px",
          fontWeight: 400,
          fontSize: "0.875rem",
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        defaultProps: {
          elevation: 0, // Removes the shadow
        },
        root: {
          backgroundColor: "#ffffff",
          // border:"1px solid #ddd",
          padding: "13px",
          boxShadow: "none",
          borderRadius: "12px",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: "2px",
          paddingBottom: "2px",
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "20px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: theme.palette.grey[500],
          "&.Mui-active": {
            color: theme.palette.primary.main,
          },
          "&.Mui-completed": {
            color: theme.palette.primary.main,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
          borderRadius: "10px",
          color: theme.palette.text.primary,
        },
        outlined: {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
          "&:hover": {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light,
            color: "#fff",
          },
        },
        contained: {
          backgroundColor: theme.palette.secondary.main,
          color: "#fff",
          "&:hover": {
            backgroundColor: theme.palette.secondary.dark,
          },
        },
        text: {
          color: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.primary.light,
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#8B8B8B",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            color: theme.palette.secondary.main,
          },
          "& .MuiInputBase-input::placeholder": {
            fontSize: "0.85rem",
            color: theme.palette.text.secondary,
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: "32px" },
        indicator: {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: "60px",
          color: "#000",
          padding: "2px 8px",
          fontSize: "16px",
          minHeight: "0px",
          marginRight: "10px",
          "&:hover": {
            color: theme.palette.primary.main,
          },
          "&.Mui-selected": {
            fontWeight: 700,
            fontSize: "16px",
            color: theme.palette.primary.main,
          },
        },
      },
    },
    // ------------------Card-------------------------
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
        },
        title: {
          fontSize: "1.125rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "24px",
          padding: "0",
          // boxShadow: "0px 7px 30px 0px rgba(21, 174, 230, 0.11)",
          backgroundColor: "#F2F7FC",
          border: "1px solid #ddd",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {},
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#000",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: 0,
          borderRadius: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          position: "sticky",
          top: 0,
          zIndex: 10,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
          zIndex: 10,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid #e5eaef`,
          fontSize: "14px",
        },
      },
    },
    MuiTableFooter: {
      styleOverrides: {
        root: {
          color: "#ffffff",
          backgroundColor: "#F2F7FC",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          color: "#808080",
          backgroundColor: "#F2F7FC ",
          borderRadius: "24px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          // height: '30px',
          "&:last-child td": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[200],
          borderRadius: "6px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f9fbff",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.grey[300],
            borderRadius: "10px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.grey[300],
            borderRadius: "10px",
          },
        },
        input: {
          padding: "10px 12px",
        },
        inputSizeSmall: {
          padding: "8px 14px",
        },
      },
    },

    //---------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        filledSuccess: {
          color: "white",
        },
        filledInfo: {
          color: "white",
        },
        filledError: {
          color: "white",
        },
        filledWarning: {
          color: "white",
        },
        standardSuccess: {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.main,
        },
        standardError: {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.main,
        },
        standardWarning: {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.main,
        },
        standardInfo: {
          backgroundColor: theme.palette.info.light,
          color: theme.palette.info.main,
        },
        outlinedSuccess: {
          borderColor: theme.palette.success.main,
          color: theme.palette.success.main,
        },
        outlinedWarning: {
          borderColor: theme.palette.warning.main,
          color: theme.palette.warning.main,
        },
        outlinedError: {
          borderColor: theme.palette.error.main,
          color: theme.palette.error.main,
        },
        outlinedInfo: {
          borderColor: theme.palette.info.main,
          color: theme.palette.info.main,
        },
      },
    },
  },
});

export { baseDarkTheme };
